import { Renderer } from "./renderer/renderer";
import { debounce } from "./lib/debounce";
import { MouseBehaviorManager } from "./lib/mousebehaviors";
import { DistanceField } from "./distance-field";
import {
  Layer,
  Scene,
  Shape,
  drawScene,
  Group,
  TextShapeInfo,
} from "./canvas-scene/canvas-scene";
import { RENDER_MODES, APP_LAUNCH_INFO } from "./launch-info";
import { Vec2 } from "./lib/math";
import { rayMarchPositions } from "./ray-march";

type Cursor = "auto" | "move";

type RayObject = "light" | "sample";

type HitTest = {
  object: RayObject;
  objectPositionOnDown: Vec2;
  offset: Vec2;
};

class RayMarchAnimationData {
  private _lightPosition: Vec2 = new Vec2(228, 248);
  private _lightSize = 30;

  private _samplePosition: Vec2 = new Vec2(451, 164);
  private _sampleSize = 30;

  public onChange: (() => void) | null = null;

  constructor(private _draggable: "both" | "lightbulb") {}

  get lightPosition(): Vec2 {
    return this._lightPosition;
  }
  get lightSize(): number {
    return this._lightSize;
  }
  get samplePosition(): Vec2 {
    return this._samplePosition;
  }
  get sampleSize(): number {
    return this._sampleSize;
  }

  set lightPosition(val: Vec2) {
    this._lightPosition = val;
    if (this.onChange != null) {
      this.onChange();
    }
  }
  set lightSize(val: number) {
    this._lightSize = val;
    if (this.onChange != null) {
      this.onChange();
    }
  }
  set samplePosition(val: Vec2) {
    this._samplePosition = val;
    if (this.onChange != null) {
      this.onChange();
    }
  }
  set sampleSize(val: number) {
    this._sampleSize = val;
    if (this.onChange != null) {
      this.onChange();
    }
  }

  hitTest(pt: Vec2): HitTest | null {
    if (pt.minus(this.lightPosition).length() < this.lightSize) {
      return {
        object: "light",
        objectPositionOnDown: this.lightPosition,
        offset: pt.minus(this.lightPosition),
      };
    } else if (
      pt.minus(this.samplePosition).length() < this.sampleSize &&
      this._draggable === "both"
    ) {
      return {
        object: "sample",
        objectPositionOnDown: this.samplePosition,
        offset: pt.minus(this.samplePosition),
      };
    }
    return null;
  }
}

import { MouseBehavior, CustomMouseEvent } from "./lib/mousebehaviors";

////////////////////////////////////////////////////////////////////////

export class SetLightPositionOnMove implements MouseBehavior {
  constructor(private _data: RayMarchAnimationData) {}

  down(_e: CustomMouseEvent): void {}
  move(e: CustomMouseEvent): void {
    this._data.lightPosition = new Vec2(e.x, e.y);
    e.accept();
  }
  up(_e: CustomMouseEvent): void {}
}

export class DragRayBehavior implements MouseBehavior {
  private _dragging: {
    object: RayObject;
    objectPositionOnDown: Vec2;
    downAt: Vec2;
    objectOffset: Vec2;
  } | null = null;

  constructor(
    private _data: RayMarchAnimationData,
    private _setCursor: (cursor: Cursor) => void
  ) {}

  down(e: CustomMouseEvent): void {
    const position = new Vec2(e.x, e.y);
    let hitTest = this._data.hitTest(position);

    if (hitTest == null) {
      if (e.metaKey) {
        // The user mouse-downed on nothing while holding cmd.
        // Move the sample px to their cursor position.
        this._data.samplePosition = position;
        hitTest = this._data.hitTest(position)!;
      } else {
        // The user mouse-downed on nothing. Move the light
        // to their cursor position.
        this._data.lightPosition = position;
        hitTest = this._data.hitTest(position)!;
      }
    }

    this._dragging = {
      object: hitTest.object,
      objectPositionOnDown: hitTest.objectPositionOnDown,
      objectOffset: hitTest.offset,
      downAt: position,
    };
    e.accept();
  }

  move(e: CustomMouseEvent): void {
    const position = new Vec2(e.x, e.y);
    const hitTest = this._data.hitTest(position);
    if (hitTest != null) {
      this._setCursor("move");
    } else {
      this._setCursor("auto");
    }

    if (this._dragging != null) {
      e.accept();

      const position = new Vec2(e.x, e.y);
      const offsetFromDown = position.minus(this._dragging.downAt);
      const newObjectPosition = this._dragging.objectPositionOnDown.plus(
        offsetFromDown
      );
      switch (this._dragging.object) {
        case "light": {
          this._data.lightPosition = newObjectPosition;
          break;
        }
        case "sample": {
          this._data.samplePosition = newObjectPosition;
          break;
        }
      }
    }
  }

  up(e: CustomMouseEvent): void {
    if (this._dragging != null) {
      e.accept();
    }
    this._dragging = null;
  }
}

// From here: https://stackoverflow.com/a/21742107
function isMobile() {
  var userAgent =
    navigator.userAgent || navigator.vendor || (window as any).opera;

  // Windows Phone must come first because its UA also contains "Android"
  if (/windows phone/i.test(userAgent)) {
    return true;
  }

  if (/android/i.test(userAgent)) {
    return true;
  }

  // iOS detection from: http://stackoverflow.com/a/9039885/177710
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return true;
  }

  if (navigator.maxTouchPoints > 0) {
    return true;
  }

  return false;
}

function lightbulbShape(x: number, y: number) {
  const shape = new Shape(
    {
      type: "lightbulb",
      center: { x, y },
      radius: 5,
    },
    { strokeStyle: "#000" }
  );
  return shape;
}

import { canvasToCssCursorProperty } from "./lib/cursor";

function createLightbulbCursor(): string {
  const size = 28;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const shape = lightbulbShape(size / 2, size / 2);
  const scene = new Scene("transparent", shape);
  const ctx = canvas.getContext("2d")!;
  drawScene(scene, ctx);
  return canvasToCssCursorProperty(canvas, size / 2, size / 2, "none");
}

function shouldDrawLightbulbWithCSS() {
  return !isMobile();
}

function getLines(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number
): string[] {
  var words = text.split(" ");
  var lines = [];
  var currentLine = words[0];

  for (var i = 1; i < words.length; i++) {
    var word = words[i];
    var width = ctx.measureText(currentLine + " " + word).width;
    if (width < maxWidth) {
      currentLine += " " + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
}

export class App {
  private _canvas: HTMLCanvasElement;
  private _inputCanvas: HTMLCanvasElement;
  private _uiCanvas: HTMLCanvasElement;

  private _renderer: Renderer;
  private _distanceFieldGenerator: DistanceField;
  private _mouseBehaviors: MouseBehaviorManager;

  // Ray animation
  private _rayMarchData = new RayMarchAnimationData(
    APP_LAUNCH_INFO.overlayType === "lightbulb" ? "lightbulb" : "both"
  );

  private _scene: Scene;

  // Layer shared between real scene and UI scene
  private _sharedLayer: Layer;

  private _modeIndex = APP_LAUNCH_INFO.renderModeIndex;

  constructor(private _canvasContainer: HTMLElement) {
    this._inputCanvas = document.createElement("canvas");
    this._uiCanvas = document.createElement("canvas");
    document.body.appendChild(this._uiCanvas);
    this._uiCanvas.style.position = "fixed";
    this._uiCanvas.style.left = "0";
    this._uiCanvas.style.top = "0";
    this._uiCanvas.style.width = "100%";
    this._uiCanvas.style.height = "100%";
    this._uiCanvas.style.pointerEvents = "none";

    const inputCtx = this._inputCanvas.getContext("2d")!;

    this._canvas = document.createElement("canvas");
    _canvasContainer.appendChild(this._canvas);
    this._renderer = new Renderer(this._canvas, this._inputCanvas);
    this._distanceFieldGenerator = new DistanceField(this._inputCanvas);

    this._mouseBehaviors = new MouseBehaviorManager();
    this._mouseBehaviors.listenOnElement(this._canvas);

    switch (APP_LAUNCH_INFO.overlayType) {
      case "none": {
        this._mouseBehaviors.register(
          new SetLightPositionOnMove(this._rayMarchData)
        );
        break;
      }
      case "lightbulb": {
        if (isMobile()) {
          this._mouseBehaviors.register(
            new DragRayBehavior(this._rayMarchData, (cursor: Cursor) => {
              this._canvas.style.cursor = cursor;
            })
          );
        } else {
          this._mouseBehaviors.register(
            new SetLightPositionOnMove(this._rayMarchData)
          );
        }

        if (shouldDrawLightbulbWithCSS()) {
          this._canvas.style.cursor = createLightbulbCursor();
        }

        this._rayMarchData.onChange = () => {
          this._renderer.setLightPosition(this._rayMarchData.lightPosition);
        };
        break;
      }
      case "ray": {
        this._mouseBehaviors.register(
          new DragRayBehavior(this._rayMarchData, (cursor: Cursor) => {
            this._canvas.style.cursor = cursor;
          })
        );
        this._rayMarchData.onChange = () => {
          this._renderer.setLightPosition(this._rayMarchData.lightPosition);
        };
        break;
      }
    }

    // Build distance field scene
    this._scene = new Scene("#fff", new Group());

    let fontSize = Math.min(
      450,
      (3 * Math.min(window.innerWidth, window.innerHeight)) / 4
    );

    if (APP_LAUNCH_INFO.editText) {
      fontSize = 100;
    }

    const sceneInfo = APP_LAUNCH_INFO.scene;
    if (sceneInfo.type === "text") {
      const ctx = inputCtx;
      const font = `${fontSize}px sans-serif`;
      ctx.font = font;
      const textSize = ctx.measureText(sceneInfo.text);

      let x = window.innerWidth / 2 - textSize.width / 2;
      let y = window.innerHeight / 2 + (fontSize * 0.7) / 2;
      if (APP_LAUNCH_INFO.editText) {
        x = 80;
        y = 80;
      }

      this._sharedLayer = new Shape(
        {
          type: "text",
          text: sceneInfo.text,
          x,
          y,
          maxWidth: APP_LAUNCH_INFO.editText
            ? window.innerWidth - 80
            : undefined,
        },
        { fillStyle: "black", font: font }
      );

      // Position light and sample
      if (APP_LAUNCH_INFO.overlayType === "ray") {
        const yValue = y - (3 * fontSize) / 5;
        const horizontalInset = textSize.width / 6;
        this._rayMarchData.lightPosition = new Vec2(
          x + horizontalInset,
          yValue
        );
        this._rayMarchData.samplePosition = new Vec2(
          x + textSize.width - horizontalInset,
          yValue
        );
      } else if (APP_LAUNCH_INFO.overlayType === "lightbulb") {
        const cSize = ctx.measureText("c");
        this._rayMarchData.lightPosition = new Vec2(
          x + textSize.width - cSize.width / 2 + 5,
          y - (4.2 * fontSize) / 16
        );
      }
    } else {
      const screenCenter = new Vec2(
        window.innerWidth / 2,
        window.innerHeight / 2
      );
      const rectSize = Math.min(window.innerWidth, window.innerHeight) / 4;
      const x = screenCenter.x - rectSize / 2;
      const y = screenCenter.y - rectSize / 2;
      this._sharedLayer = new Shape(
        {
          type: "rect",
          x,
          y,
          width: rectSize,
          height: rectSize,
        },
        { fillStyle: "#000" }
      );

      // Position light and sample
      if (APP_LAUNCH_INFO.overlayType === "ray") {
        const xOutset = 80;
        const yOffset = 5;
        this._rayMarchData.lightPosition = new Vec2(x - xOutset, y - yOffset);
        this._rayMarchData.samplePosition = new Vec2(
          x + rectSize + xOutset,
          y - yOffset
        );
      } else if (APP_LAUNCH_INFO.overlayType === "lightbulb") {
        const xOutset = 80;
        this._rayMarchData.lightPosition = new Vec2(
          x - xOutset,
          y + rectSize / 2
        );
      }
    }
    this._scene.root.appendChild(this._sharedLayer);

    this.onResize();
    if (APP_LAUNCH_INFO.editText) {
      if (APP_LAUNCH_INFO.scene.type !== "text") {
        throw new Error(`editText param supported only when scene is text`);
      }
      const lowercase = "abcdefghijklmnopqrstuvwxyz";
      const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
      const KEY_A = 65;
      const KEY_Z = 90;
      const KEY_BACKSPACE = 8;
      const KEY_SPACE = 32;

      const textInfo = this._sharedLayer.shape()!.shape as TextShapeInfo;

      document.addEventListener("keydown", (e: KeyboardEvent) => {
        if (e.keyCode === KEY_BACKSPACE) {
          textInfo.text = textInfo.text.substring(0, textInfo.text.length - 1);
        } else if (e.key === "Shift") {
          // Do nothing
        } else {
          textInfo.text += e.key;
        }

        const ctx = this._inputCanvas.getContext("2d")!;

        const w = this._inputCanvas.width;
        const h = this._inputCanvas.height;
        const fontSize = 85;
        ctx.font = `${fontSize}px sans-serif`;
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, w, h);

        ctx.fillStyle = "black";
        if (textInfo.text.length === 0) {
          ctx.fillRect(0, 0, 1, 1);
        } else {
          const lines = getLines(
            ctx,
            textInfo.text,
            window.innerWidth - 80 * 2
          );
          for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            ctx.fillText(line, 80, 140 + fontSize * i);
          }
        }
        this._distanceFieldGenerator.setNeedsUpdate();
      });
    }

    window.addEventListener("resize", this.debouncedResize);

    requestAnimationFrame(this.onFrame);
  }

  teardown = () => {
    window.removeEventListener("resize", this.onResize);
    // TODO(ryan): tear down mouse behaviors
  };

  onResize = () => {
    const box = this._canvasContainer.getBoundingClientRect();
    this._canvas.width = box.width;
    this._canvas.height = box.height;

    this._inputCanvas.width = this._canvas.width;
    this._inputCanvas.height = this._canvas.height;
    this._uiCanvas.width = this._canvas.width;
    this._uiCanvas.height = this._canvas.height;
    drawScene(this._scene, this._inputCanvas.getContext("2d")!);

    this._distanceFieldGenerator.updateForScreenResize();
    this._renderer.handleWindowResize();
  };

  debouncedResize = debounce(this.onResize);

  _drawUILightbulb() {
    const ctx = this._uiCanvas.getContext("2d")!;

    const { lightPosition } = this._rayMarchData;

    // Build UI scene
    const uiScene = new Scene("transparent", new Group());
    uiScene.root.appendChild(this._sharedLayer);
    uiScene.root.appendChild(lightbulbShape(lightPosition.x, lightPosition.y));

    drawScene(uiScene, ctx);
  }

  _drawUIRayMarchDetails() {
    const ctx = this._uiCanvas.getContext("2d")!;

    const { samplePosition, lightPosition } = this._rayMarchData;

    // Build UI scene
    const uiScene = new Scene("transparent", new Group());
    uiScene.root.appendChild(this._sharedLayer);

    const rayStart = samplePosition;
    const nudgeForward = lightPosition.minus(samplePosition).normalized();
    const rayStop = lightPosition.plus(nudgeForward.times(-20));
    uiScene.root.appendChild(
      new Shape(
        {
          type: "ray",
          origin: rayStart.toObject(),
          dest: rayStop.toObject(),
        },
        { strokeStyle: "red", lineWidth: 2 }
      )
    );

    const positions = rayMarchPositions(
      samplePosition,
      lightPosition,
      this._distanceFieldGenerator.cpuSampler()
    );
    for (let position of positions) {
      const circle = new Shape(
        {
          type: "circle",
          center: position.position.toObject(),
          radius: position.radius,
        },
        { strokeStyle: "#0FF", lineWidth: 1 }
      );
      uiScene.root.appendChild(circle);
    }

    const shape = new Shape(
      {
        type: "lightbulb",
        center: { x: lightPosition.x, y: lightPosition.y },
        radius: 5,
      },
      { strokeStyle: "#000" }
    );
    uiScene.root.appendChild(shape);

    const pixel = this._renderer.readPixel(samplePosition.x, samplePosition.y);
    const size = 20;
    const sampleRect = new Shape(
      {
        type: "rect",
        x: samplePosition.x - size / 2,
        y: samplePosition.y - size / 2,
        width: size,
        height: size,
      },
      {
        fillStyle: `rgba(${pixel[0]}, ${pixel[1]}, ${pixel[2]}, 1)`,
        strokeStyle: "#f00",
      }
    );
    uiScene.root.appendChild(sampleRect);

    drawScene(uiScene, ctx);
  }

  onFrame = () => {
    if (this._distanceFieldGenerator.needsUpdate()) {
      this._distanceFieldGenerator.update();
      this._renderer.setDistanceField(this._distanceFieldGenerator.canvas());
    }

    switch (APP_LAUNCH_INFO.overlayType) {
      case "none": {
        break;
      }
      case "lightbulb": {
        if (!shouldDrawLightbulbWithCSS()) {
          this._drawUILightbulb();
        }
        break;
      }
      case "ray": {
        this._drawUIRayMarchDetails();
        break;
      }
    }

    this._renderer.draw(RENDER_MODES[this._modeIndex]);
    requestAnimationFrame(this.onFrame);
  };
}
